// PresetManager — collects state from every UI module, applies stored
// presets, and persists user presets to localStorage. The manager
// holds references to each module so it can call `getState()` /
// `setState()` per module; synth-ui wires this up after all modules
// are constructed.

import type { OscModule } from "../ui/osc-module.ts";
import type { FilterModule } from "../ui/filter-module.ts";
import type { EnvModule } from "../ui/env-module.ts";
import type { ModEnvModule } from "../ui/mod-env-module.ts";
import type { LfoModule } from "../ui/lfo-module.ts";
import type { EqModule } from "../ui/eq-module.ts";
import type { DistortionModule } from "../ui/distortion-module.ts";
import type { DelayModule } from "../ui/delay-module.ts";
import type { ReverbModule } from "../ui/reverb-module.ts";
import type { MasterModule } from "../ui/master-module.ts";
import type { FxRack } from "../ui/fx-rack.ts";
import { FACTORY_PRESETS } from "./factory.ts";
import {
  PRESET_VERSION,
  loadUserPresets,
  saveUserPresets,
  type Preset,
  type PresetSource,
} from "./preset.ts";

export interface PresetManagerModules {
  osc1: OscModule;
  osc2: OscModule;
  filter: FilterModule;
  ampEnv: EnvModule;
  modEnv: ModEnvModule;
  lfo: LfoModule;
  eq: EqModule;
  distortion: DistortionModule;
  delay: DelayModule;
  reverb: ReverbModule;
  master: MasterModule;
  fxRack: FxRack;
  /** Bpm lives in the header, not a module — so the manager owns its
   *  getter and an applier. The applier should both update the
   *  header's tempo knob visual AND push the engine value. */
  getBpm: () => number;
  setBpm: (bpm: number) => void;
}

export interface PresetEntry {
  source: PresetSource;
  preset: Preset;
}

export class PresetManager {
  private modules: PresetManagerModules;
  private userPresets: Preset[];
  private listeners: Array<() => void> = [];
  /** Name of the most recently loaded preset, for the UI to display.
   *  Empty when the user has made changes since the last load. */
  private currentName = "init";
  private dirty = false;
  /** True while `apply()` is restoring module state. Each `setState`
   *  call cascades through control onChange handlers which would
   *  otherwise call `markDirty()` and flip the bar's display to
   *  "dirty" the instant a preset loads. */
  private applying = false;

  constructor(modules: PresetManagerModules) {
    this.modules = modules;
    this.userPresets = loadUserPresets();
  }

  // ----- queries -----

  /** Get every preset visible in the UI, in display order:
   *  factories first, then user presets alphabetically. */
  list(): PresetEntry[] {
    const factories: PresetEntry[] = FACTORY_PRESETS.map((p) => ({
      source: "factory",
      preset: p,
    }));
    const users: PresetEntry[] = [...this.userPresets]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ source: "user", preset: p }));
    return [...factories, ...users];
  }

  currentPresetName(): string {
    return this.currentName;
  }

  isDirty(): boolean {
    return this.dirty;
  }

  // ----- snapshot -----

  /** Build a preset from current module state. Used by save / save-as. */
  snapshot(name: string): Preset {
    const m = this.modules;
    return {
      version: PRESET_VERSION,
      name,
      osc1: m.osc1.getState(),
      osc2: m.osc2.getState(),
      filter: m.filter.getState(),
      ampEnv: m.ampEnv.getState(),
      modEnv: m.modEnv.getState(),
      lfo: m.lfo.getState(),
      bpm: m.getBpm(),
      eq: m.eq.getState(),
      distortion: m.distortion.getState(),
      delay: m.delay.getState(),
      reverb: m.reverb.getState(),
      master: m.master.getState(),
      fxOrder: m.fxRack.getOrder(),
    };
  }

  // ----- apply -----

  /** Apply a stored preset to every module + global. Each module's
   *  `setState` updates its visuals and pushes to the engine via the
   *  control onChange wiring it was constructed with. */
  apply(preset: Preset): void {
    const m = this.modules;
    this.applying = true;
    try {
      m.osc1.setState(preset.osc1);
      m.osc2.setState(preset.osc2);
      m.filter.setState(preset.filter);
      m.ampEnv.setState(preset.ampEnv);
      m.modEnv.setState(preset.modEnv);
      m.lfo.setState(preset.lfo);
      m.setBpm(preset.bpm);
      m.eq.setState(preset.eq);
      m.distortion.setState(preset.distortion);
      m.delay.setState(preset.delay);
      m.reverb.setState(preset.reverb);
      m.master.setState(preset.master);
      m.fxRack.setOrder(preset.fxOrder);
    } finally {
      this.applying = false;
    }

    this.currentName = preset.name;
    this.dirty = false;
    this.emit();
  }

  // ----- save / delete -----

  /** Save current state under the given name. Replaces an existing user
   *  preset of the same name; never overwrites a factory. */
  saveAs(name: string): void {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (FACTORY_PRESETS.some((p) => p.name === trimmed)) {
      console.warn("[preset] cannot overwrite factory preset", trimmed);
      return;
    }
    const next = this.snapshot(trimmed);
    const idx = this.userPresets.findIndex((p) => p.name === trimmed);
    if (idx >= 0) {
      this.userPresets[idx] = next;
    } else {
      this.userPresets.push(next);
    }
    saveUserPresets(this.userPresets);
    this.currentName = trimmed;
    this.dirty = false;
    this.emit();
  }

  /** Delete a user preset by name. No-op for factory presets. */
  delete(name: string): void {
    const before = this.userPresets.length;
    this.userPresets = this.userPresets.filter((p) => p.name !== name);
    if (this.userPresets.length !== before) {
      saveUserPresets(this.userPresets);
      this.emit();
    }
  }

  /** Mark current state as "dirty" — the user has changed a control
   *  since the last load. Called externally by the UI on any control
   *  change to keep the preset bar's display honest. */
  markDirty(): void {
    if (this.applying || this.dirty) return;
    this.dirty = true;
    this.emit();
  }

  // ----- listeners -----

  /** Subscribe to manager state changes (preset loaded, saved, deleted,
   *  marked dirty). Used by the preset bar UI to refresh its dropdown
   *  + label. Returns an unsubscribe function. */
  subscribe(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}
